import { Backdrop, Box, CircularProgress, Drawer, Grid, Typography } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { IntroSnackbar } from 'components/facets/IntroSnackbar'
import { useUser } from 'components/providers/UserProvider'
import { theme } from 'config'
import { localDB } from 'config/localforage'
import robotImg from 'img/robot-2.png'
import {
  changeBotActivation,
  changePricingBotActivation,
  createUserSubEntity,
  createUserSubEntityWithId,
  deleteUserSubEntity,
  editUserSubEntity,
  getPriceUpdateArticles,
  getPriceUpdates,
  getUser,
  recordUsage,
  runManualPriceUpdate,
  runTestPriceUpdate,
} from 'lib/api'
import { ApiCallStatus, useApiCall } from 'lib/hooks/useApiCall'
import { useIntroSnackbar } from 'lib/hooks/useIntroSnackbar'
import { currentCardmarketAccount, getUniqueNewName } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity,
  createInventoryArticle,
  InventoryArticle,
  PricingPlan,
  PRICING_DEFAULTS,
  User,
} from 'shared'
import { v4 as uuid } from 'uuid'
import { PriceUpdatePresenter } from '../../PriceUpdates/PriceUpdatesPresenter'
import { PricingPlanPresenter } from '../../PricingPlan/PricingPlanPresenter'
import { CreatePlanPresenter } from './CreatePlanPresenter'
import { IntroCreatePlanPresenter } from './IntroCreatePlanPresenter'
import { PricingBotActivityPresenter } from './PricingBotActivityPresenter'
import { PricingPlanCard } from './PricingPlanCard'

export interface PricingBotPresenterProps {
  handleChange: (apiRequest: () => Promise<Partial<User>>) => Promise<boolean>
}

export interface StockState {
  fetchedAt: string
  stock: InventoryArticle[]
  sealedStock: InventoryArticle[]
  entityId?: string
}

const usePriceUpdateArticles = (
  updateId: string | null
): [StockState, (newValue: StockState | ((arg: StockState) => StockState)) => void, boolean] => {
  const priceUpdateArticlesCall = useApiCall<typeof getPriceUpdateArticles>(
    getPriceUpdateArticles,
    undefined,
    true
  )

  const [loading, setLoading] = useState(false)

  const [inventoryState, setInventoryState] = useState<StockState>({
    stock: [],
    sealedStock: [] as InventoryArticle[],
    fetchedAt: new Date().toISOString(),
    entityId: String(updateId),
  })

  useEffect(() => {
    if (updateId) {
      setLoading(true)

      localDB.getItem(`update-${updateId}`).then((value: any) => {
        if (!value?.stock?.length) {
          priceUpdateArticlesCall.performCall(updateId)
        } else {
          try {
            const hydrated = {
              //@ts-ignore
              ...value,
              entityId: updateId,
              stock: value.stock.map((article: any) => {
                if (article.card.expansion.name) {
                  article.card.expansion = article.card.expansion.name
                }
                return createInventoryArticle(article)
              }),
            }

            setInventoryState(hydrated)
          } catch (err) {
            console.log('incorrect cached value')
          }
        }

        setLoading(false)
      })
    } else {
      setInventoryState({
        stock: [],
        sealedStock: [] as InventoryArticle[],
        fetchedAt: new Date().toISOString(),
      })
    }
  }, [updateId])

  useEffect(() => {
    if (priceUpdateArticlesCall.status === ApiCallStatus.Success) {
      const stockData = priceUpdateArticlesCall.data.map((article: any) => {
        if (article.card.expansion.name) {
          article.card.expansion = article.card.expansion.name
        }
        return article
      })
      const newInventoryStock = {
        ...inventoryState,
        stock: stockData,
        entityId: updateId!,
      }
      localDB.setItem(`update-${updateId}`, newInventoryStock)
      setInventoryState(newInventoryStock)
    }
  }, [priceUpdateArticlesCall.status])

  return [
    inventoryState,
    setInventoryState,
    loading || priceUpdateArticlesCall.status === ApiCallStatus.Fetching,
  ]
}

const IntroContent = () => {
  const { t } = useTranslation()

  return (
    <Box>
      <Box>
        <Typography variant="body1">
          <b>{t('priceUpdates.yourBot')}</b>
        </Typography>
      </Box>
      <Box>
        <Typography variant="body2" gutterBottom>
          {t('priceUpdates.howTo1')}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {t('priceUpdates.howTo2')}
        </Typography>{' '}
        <Typography variant="body2" gutterBottom>
          {t('priceUpdates.howTo3')}
        </Typography>
      </Box>
    </Box>
  )
}

export const PricingBotPresenter = ({ handleChange }: PricingBotPresenterProps): JSX.Element => {
  const [pricingPlanCtx, setPricingPlanCtx] = useState<PricingPlan | null>(null)
  const { user, activeGame, setUser } = useUser()
  const { t } = useTranslation()
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false)
  const [introCreatePlanDialogOpen, setIntroCreatePlanDialogOpen] = useState(false)
  const [createPlanLoading, setCreatePlanLoading] = useState(false)
  // When set, the PricingPlanCard for the plan with that _id mounts with
  // the new-UI drawer already open. Used to auto-open the drawer right
  // after creating a new plan via "Create new pricing program".
  const [newlyCreatedPlanId, setNewlyCreatedPlanId] = useState<string | null>(null)
  const [planIdTestRunning, setPlanIdTestRunning] = useState<string>('')
  const [planIdManualRunning, setPlanIdManualRunning] = useState<string>('')
  const { enqueueSnackbar } = useSnackbar()

  useIntroSnackbar('pricing-bot-intro', <IntroContent />)
  if (!user) return <></>

  // Beta gating: a user is in the new pricing bot UI iff the explicit
  // featureFlag is set. The opt-in (script or backoffice) flattens their
  // pricingPlans in place + sets the flag, so the new UI reads the same
  // pricingPlans array as everyone else. Users outside the beta keep the
  // legacy flow (creation dialog + PricingPlanPresenter drawer).
  const isPricingBotBeta = user.featureFlags?.pricingBotBeta === true

  const pricingPlans = user.pricingPlans.filter((plan) => plan.idGame === activeGame.idGame)

  const onPricingPlanChange = useCallback(
    async (pricingPlan: PricingPlan): Promise<void> => {
      setPricingPlanCtx(pricingPlan)
      //handleChange(() => editUserSubEntity(pricingPlan, 'pricingPlans'), 'pricingPlans')
    },
    [setPricingPlanCtx]
  )

  // const activePlanId = user.activeBots.find(
  //   (bot) => bot.account === user.cardmarketUsername && bot.idGame === activeGame.idGame
  // )?.planId

  const [activePlanId, setActivePlanId] = useState(
    user.cardmarketUsername
      ? user.activeBots.find(
          (bot) => bot.account === user.cardmarketUsername && bot.idGame === activeGame.idGame
        )?.planId
      : user.pricingBotActivations?.find((entry) => entry.idGame === activeGame.idGame)?.planId
  )

  const activeGamePricingPlans = user.pricingPlans.filter(
    (plan) => plan.idGame === activeGame.idGame
  )

  // const activePlan = activeGamePricingPlans.find((plan) => plan._id === activePlanId)

  const [activePlan, setActivePlan] = useState(
    activeGamePricingPlans.find((plan) => plan._id === activePlanId)
  )

  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    const updateBotActivation = async () => {
      setActivePlanId(activePlan?._id)
      try {
        if (user.cardmarketUsername) {
          await changeBotActivation(
            currentCardmarketAccount(user)!,
            activeGame.idGame,
            activePlan ? activePlan._id : 'none'
          )
        } else {
          await changePricingBotActivation(activeGame.idGame, activePlan ? activePlan._id : 'none')
        }
      } catch (err) {
        enqueueSnackbar(t('error.generic'), { variant: 'error' })
      }
      const newUser = await getUser()
      setUser(newUser)
      setShowSpinner(false)
    }

    setShowSpinner(true)
    updateBotActivation()
  }, [activePlan])

  useEffect(() => {
    setActivePlan(activeGamePricingPlans.find((plan) => plan._id === activePlanId))
  }, [activePlanId])

  const [selectedPlanId, setSelectedPlanId] = useState(
    (activePlan?._id || activeGamePricingPlans?.[0]?._id) ?? ''
  )

  useEffect(() => {
    if (!activeGamePricingPlans.find(({ _id }) => _id === selectedPlanId)) {
      setSelectedPlanId((activePlan?._id || activeGamePricingPlans?.[0]?._id) ?? '')
    }
  }, [activeGamePricingPlans.length])

  const pricingPlanJson = JSON.stringify(pricingPlanCtx)

  useEffect(() => {
    let timeout: any = null
    if (pricingPlanCtx?._id) {
      timeout = setTimeout(
        () => handleChange(() => editUserSubEntity(pricingPlanCtx, 'pricingPlans')),
        300
      )
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [pricingPlanJson])

  const [showBotRun, setShowBotRun] = useState<string | null>(null)

  const [priceUpdateArticles, , loading] = usePriceUpdateArticles(showBotRun)

  const onPriceUpdateClose = useCallback(() => {
    setShowBotRun(null)
  }, [setShowBotRun])

  const priceUpdatesCall = useApiCall<typeof getPriceUpdates>(getPriceUpdates, [activeGame.idGame])

  useEffect(() => {
    const pollingInterval = setInterval(() => {
      priceUpdatesCall.performCall(activeGame.idGame)
    }, 5000)

    return () => {
      clearInterval(pollingInterval)
    }
  }, [])

  const shownBotRun = priceUpdatesCall.data?.updates.find((update) => update._id === showBotRun)

  const onPlanEdit = useCallback((plan) => setPricingPlanCtx(plan), [setPricingPlanCtx])

  const onTest = async (testPlan: PricingPlan) => {
    try {
      setPlanIdTestRunning(testPlan._id)
      await runTestPriceUpdate(testPlan._id, activeGame.idGame)
      recordUsage(Activity.BotTestRun, {
        plan: pricingPlans.find((plan) => plan._id === testPlan._id),
      })
    } catch (e) {
      enqueueSnackbar(t('error.generic'), { variant: 'error' })
    }
  }

  const onManualRun = async (plan: PricingPlan) => {
    try {
      setPlanIdManualRunning(plan._id)
      await runManualPriceUpdate(plan._id, activeGame.idGame)
    } catch (e) {
      enqueueSnackbar(t('error.generic'), { variant: 'error' })
    }
  }

  const onPlanCreate = useCallback(
    async (plan) => {
      setCreatePlanDialogOpen(false)
      try {
        await handleChange(async () => {
          const response = await createUserSubEntity(
            {
              name: getUniqueNewName(`${activeGame.name} Program`, user?.pricingPlans),
              idGame: activeGame.idGame,
              settings: {
                maxValueDecrease: PRICING_DEFAULTS.maxDecrease,
                maxValueIncrease: PRICING_DEFAULTS.maxIncrease,
              },
              defaultNode: { method: undefined, id: 'default' },
              nodes: [],
              disallowNesting: true,
              ...plan,
            } as Omit<PricingPlan, '_id'>,
            'pricingPlans'
          )
          //#$ remove ignores
          //@ts-ignore
          if (response?.[response?.length - 1]) {
            //@ts-ignore
            setPricingPlanCtx(response?.[response?.length - 1] as PricingPlan)
          }
          return response
        })
      } finally {
      }
    },
    [handleChange, activeGame.idGame, user.pricingPlans.length]
  )

  // Creates a new (flat) pricing plan directly in pricingPlans. Triggered
  // from "Create new pricing program" in the beta UI — no standard/advanced
  // choice dialog (inline spinner during the await). On completion,
  // setNewlyCreatedPlanId → the matching PricingPlanCard auto-opens the
  // new-UI drawer on the next render.
  const onPlanCreateBeta = useCallback(async () => {
    setCreatePlanLoading(true)
    const sharedId = uuid()
    const basePlan = {
      name: getUniqueNewName(`${activeGame.name} Program`, user?.pricingPlans),
      idGame: activeGame.idGame,
      settings: {
        maxValueDecrease: PRICING_DEFAULTS.maxDecrease,
        maxValueIncrease: PRICING_DEFAULTS.maxIncrease,
        maxValueIncreaseEnabled: true,
      },
      defaultNode: { method: undefined, id: 'default' },
      nodes: [],
      disallowNesting: true,
    } as Omit<PricingPlan, '_id'>

    try {
      await handleChange(() => createUserSubEntityWithId(sharedId, basePlan, 'pricingPlans'))
      setNewlyCreatedPlanId(sharedId)
    } finally {
      setCreatePlanLoading(false)
    }
  }, [handleChange, activeGame.idGame, activeGame.name, user?.pricingPlans])

  // Beta → create the (flat) plan directly and open the new drawer.
  // Non-beta → open the legacy dialog flow (intro → CreatePlanPresenter).
  const onCreatePlanClick = useCallback(() => {
    if (isPricingBotBeta) onPlanCreateBeta()
    else setIntroCreatePlanDialogOpen(true)
  }, [isPricingBotBeta, onPlanCreateBeta])

  const onStrategyClone = useCallback(
    async (plan) => {
      try {
        await handleChange(() =>
          createUserSubEntity(
            {
              ...plan,
              name: getUniqueNewName(`New Pricing Program`, user?.pricingPlans),
              idGame: activeGame.idGame,
              settings: {
                maxValueDecrease: PRICING_DEFAULTS.maxDecrease,
                maxValueIncrease: PRICING_DEFAULTS.maxIncrease,
              },
            } as Omit<PricingPlan, '_id'>,
            'pricingPlans'
          )
        )
      } finally {
      }
    },
    [handleChange, activeGame.idGame]
  )

  const onStrategyChange = useCallback(
    async (pricingStrategy) => {
      handleChange(() => editUserSubEntity(pricingStrategy, 'pricingStrategies'))
    },
    [handleChange]
  )

  const onStrategyDelete = useCallback(
    async (id) => {
      handleChange(() => deleteUserSubEntity(id, 'pricingStrategies'))
    },
    [handleChange]
  )

  const onPricingPlanDrawerClose = useCallback(() => setPricingPlanCtx(null), [setPricingPlanCtx])

  // const { isMobile } = useAppContext()

  return (
    <>
      {showSpinner && (
        <Backdrop
          open={showSpinner}
          style={{ color: theme.palette.lightGray, zIndex: theme.zIndex.modal }}
        >
          <CircularProgress />
        </Backdrop>
      )}
      <div>
        {activeGamePricingPlans.length > 0 && (
          <Box padding={2}>
            <Box display="flex" alignItems={'center'}>
              <Box>
                <Typography variant="h5" style={{ textTransform: 'uppercase' }}>
                  <b>
                    BOT {activePlan ? 'online' : 'offline'}
                    {activePlan && `: ${activePlan.name}`}
                  </b>
                </Typography>
                <Box marginTop={1}>
                  <Typography variant="body2">
                    {t('priceUpdates.planSettings.sealedWarning')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {activeGamePricingPlans.length === 0 ? (
          <>
            <div className="w-100 d-flex flex-column align-items-center mt-5">
              <img src={robotImg} style={{ maxWidth: 600, width: '100%', height: 'auto' }} />
              <Box maxWidth={750} width="100%" px={2}>
                <h2 className="fst-italic mt-3 text-center">{t('priceUpdates.intro')}</h2>
              </Box>
              <Button
                scaleUp
                variant="contained"
                color="primary"
                className="mt-2"
                disabled={createPlanLoading}
                startIcon={
                  createPlanLoading ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                onClick={onCreatePlanClick}
              >
                {t('priceUpdates.createNewPlan')}
              </Button>
            </div>
          </>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                disabled={createPlanLoading}
                startIcon={
                  createPlanLoading ? <CircularProgress size={16} color="inherit" /> : undefined
                }
                onClick={onCreatePlanClick}
              >
                {t('priceUpdates.createNewPlan')}
              </Button>
              <Box>
                {activeGamePricingPlans.map((plan) => (
                  <Box key={plan._id} marginY={2} className="active-pricing-bot-container">
                    <PricingPlanCard
                      plan={plan}
                      onEdit={onPlanEdit}
                      activePlan={activePlan}
                      setActivePlan={setActivePlan}
                      onTest={onTest}
                      onRun={onManualRun}
                      isTestRunning={planIdTestRunning === plan._id}
                      isManualRunning={planIdManualRunning === plan._id}
                      isPricingBotBeta={isPricingBotBeta}
                      defaultPreviewOpen={plan._id === newlyCreatedPlanId}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <PricingBotActivityPresenter
                onViewUpdate={(updateId) => setShowBotRun(updateId)}
                activePlan={activePlan}
                selectedPlanId={selectedPlanId}
                setSelectedPlanId={setSelectedPlanId}
                isTestRunning={planIdTestRunning !== ''}
                setIsTestRunning={setPlanIdTestRunning}
                isManualRunning={planIdManualRunning !== ''}
                setIsManualRunning={setPlanIdManualRunning}
                updates={
                  priceUpdatesCall.data?.updates
                    ?.filter(
                      (update) =>
                        update.details?.cardmarketUsername === user.cardmarketUsername &&
                        update.idGame === activeGame.idGame
                    )
                    .sort(
                      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    ) ?? []
                }
              />
            </Grid>
          </Grid>
        )}
      </div>

      <Drawer
        disableEnforceFocus
        onClose={() => setPricingPlanCtx(null)}
        anchor={'right'}
        open={!!pricingPlanCtx}
      >
        {!!pricingPlanCtx && (
          <Box width={'100vw'} minWidth={'50rem'}>
            <PricingPlanPresenter
              onClose={onPricingPlanDrawerClose}
              pricingPlan={pricingPlanCtx}
              onChange={onPricingPlanChange}
              onStrategyDelete={onStrategyDelete}
              onStrategyChange={onStrategyChange}
              onStrategyClone={onStrategyClone}
            />
          </Box>
        )}
      </Drawer>

      <Drawer open={!!showBotRun} anchor="right" onClose={() => setShowBotRun(null)}>
        {showBotRun && (
          <Box width={'calc(100vw - 83px)'}>
            <PriceUpdatePresenter
              onClose={onPriceUpdateClose}
              articles={priceUpdateArticles.stock ?? []}
              loading={loading}
              updateType={shownBotRun?.details?.type}
              updateId={priceUpdateArticles.entityId}
            />
          </Box>
        )}
      </Drawer>

      <ClosableDialog
        maxWidth="md"
        open={introCreatePlanDialogOpen}
        onClose={() => setIntroCreatePlanDialogOpen(false)}
      >
        {introCreatePlanDialogOpen && (
          <IntroCreatePlanPresenter
            setIntroCreatePlanDialogOpen={setIntroCreatePlanDialogOpen}
            setCreatePlanDialogOpen={setCreatePlanDialogOpen}
          />
        )}
      </ClosableDialog>

      <ClosableDialog
        maxWidth="lg"
        open={createPlanDialogOpen}
        onClose={() => setCreatePlanDialogOpen(false)}
      >
        {createPlanDialogOpen && <CreatePlanPresenter onCreate={onPlanCreate} />}
      </ClosableDialog>

      <IntroSnackbar />
    </>
  )
}
